// =====================================================================
// AdminGlobalTimesheets.jsx
// Replica "Toutes les feuilles de temps" din Odoo
// Grid users × days + click cell → modal detalii zi
// =====================================================================
import { useState, useEffect, useMemo } from 'react';
import { timesheetsGlobal, users as usersApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { marqueurInactif } from '../../lib/tri-personnes.js';

const STATUS_COLORS = {
  DRAFT: '#fbbf24',
  SUBMITTED: '#60a5fa',
  APPROVED: '#34d399',
  REJECTED: '#f87171',
};

function ymd(date) { return date.toISOString().slice(0, 10); }
function parseYMD(str) { return new Date(str + 'T00:00:00Z'); }
function addDays(date, n) { const d = new Date(date); d.setUTCDate(d.getUTCDate() + n); return d; }
function startOfMonth(d) { return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)); }
function endOfMonth(d) { return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)); }
function isWeekend(dateStr) { const d = parseYMD(dateStr); const dow = d.getUTCDay(); return dow === 0 || dow === 6; }
function formatMin(min) { if (!min) return '0:00'; const h = Math.floor(min / 60); const m = min % 60; return `${h}:${m.toString().padStart(2, '0')}`; }
// Heures décimales → "3h" / "3h30" (décisions HR overtime)
function fmtHours(h) { if (!h) return '0h'; const hh = Math.floor(h); const mm = Math.round((h - hh) * 60); return mm ? `${hh}h${String(mm).padStart(2, '0')}` : `${hh}h`; }
// Day 11 — code couleur des heures journalières: 0 neutre, <7:36 (456min) rouge, 7:36–8:00 (480min) vert, >8:00 bleu
function colorForMin(min) {
  if (!min || min === 0) return '#d1d5db';   // neutre (week-end / absent)
  if (min < 456) return '#dc2626';           // rouge — sous la journée normale
  if (min <= 480) return '#16a34a';          // vert — journée normale (7:36 à 8:00)
  return '#2563eb';                          // bleu — au-delà de 8:00 (distinct du rouge)
}

// Day 11 (28-05-2026) — Mapping absences code → label + TEXT color only
// Le fond de la cellule reste neutre (blanc/gris weekend) — seul le texte est coloré
const ABSENCE_STYLE = {
  JF:       { label: 'JF',    color: '#475569' },   // slate - jour férié
  VA:       { label: 'VA',    color: '#16a34a' },   // green - Conges payes
  'VA-0.5': { label: 'VA',    color: '#16a34a' },   // green half-day
  VAN:      { label: 'VAN',   color: '#16a34a' },   // green - Vacances non payees
  VAEN1:    { label: 'VAEN1', color: '#0891b2' },   // cyan dark - Vacances reportees
  AA:       { label: 'AA',    color: '#7c3aed' },   // purple - Absence autorisee
  'AA-0.5': { label: 'AA',    color: '#7c3aed' },   // purple half-day
  REC:      { label: 'REC',   color: '#d97706' },   // amber dark - Recuperation
  MAT:      { label: 'MAT',   color: '#be185d' },   // pink dark - Maternite
  PAT:      { label: 'PAT',   color: '#1d4ed8' },   // blue dark - Paternite
  MAL:      { label: 'MAL',   color: '#ea580c' },   // orange dark - Maladie
  CSS:      { label: 'CSS',   color: '#b91c1c' },   // red dark
  CSSO:     { label: 'CSSO',  color: '#b91c1c' },   // red dark
  HC:       { label: 'HC',    color: '#94a3b8' },   // slate muted — Hors Contrat (pas programmé)
};
function absenceStyle(code) {
  return ABSENCE_STYLE[code] || { label: code, color: '#6b7280' };
}
const STANDARD_DAY_MIN = 456; // 7h36 = journée standard
const ABSENCE_LEGEND = {
  JF:    'Jour férié',
  VA:    'Congés payés',
  VAN:   'Vacances non payées',
  VAEN1: 'Vacances reportées',
  AA:    'Absence autorisée',
  REC:   'Récupération',
  MAT:   'Maternité',
  PAT:   'Paternité',
  MAL:   'Maladie',
  CSS:   'Congé sans solde',
  HC:    'Hors contrat (non programmé)',
};

const DAYS_FR = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
const MONTHS_FR_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

// Day 14 — pointage « fait » (déclaration salaire), réservé à ces utilisateurs.
// Case à cocher → ligne grisée. Persistance localStorage par utilisateur + période (perso, par navigateur).
const PAYROLL_USERS = ['Silviu', 'Luca', 'Florent', 'Mathieu'];

export default function AdminGlobalTimesheets({ embedded = false }) {
  const { can, user } = useAuth();
  const canMask = can('users.update.all');
  const canPayrollCheck = PAYROLL_USERS.includes(user?.username);
  const [showMasked, setShowMasked] = useState(false);
  const today = useMemo(() => parseYMD(new Date().toISOString().slice(0, 10)), []);
  const [periodStart, setPeriodStart] = useState(() => startOfMonth(today));
  const [periodEnd, setPeriodEnd] = useState(() => endOfMonth(today));

  // pointage « fait » (localStorage par utilisateur + période)
  const payrollKey = canPayrollCheck ? `payroll-fait:${user?.username}:${ymd(periodStart)}` : null;
  const [doneSet, setDoneSet] = useState(() => new Set());
  useEffect(() => {
    if (!payrollKey) { setDoneSet(new Set()); return; }
    try { const raw = localStorage.getItem(payrollKey); setDoneSet(new Set(raw ? JSON.parse(raw) : [])); }
    catch { setDoneSet(new Set()); }
  }, [payrollKey]);
  function toggleDone(userId) {
    const next = new Set(doneSet);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    setDoneSet(next);
    if (payrollKey) { try { localStorage.setItem(payrollKey, JSON.stringify([...next])); } catch { /* quota */ } }
  }
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [dayModal, setDayModal] = useState(null); // { userId, userName, date }

  // Day 13+ (export période) — export XLSX de TOUTES les feuilles de temps sur une période
  // choisie (tous les utilisateurs, y compris masqués). Réutilise /api/admin/export/global.
  const canExport = can('users.read.all');
  const [exportFrom, setExportFrom] = useState(() => ymd(startOfMonth(today)));
  const [exportTo, setExportTo]     = useState(() => ymd(endOfMonth(today)));
  const [exporting, setExporting]   = useState(false);
  async function handleExportPeriod() {
    if (!exportFrom || !exportTo) { alert('Sélectionnez la période (du / au).'); return; }
    if (exportFrom > exportTo)   { alert('« Du » doit être antérieur ou égal à « Au ».'); return; }
    setExporting(true);
    try {
      const resp = await fetch(`/api/admin/export/global?from=${exportFrom}&to=${exportTo}&only=ts`, { credentials: 'include' });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brain-elog-feuilles-${exportFrom}_${exportTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur export: ' + err.message);
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    timesheetsGlobal.getGrid(ymd(periodStart), ymd(periodEnd), showMasked).then(data => setData(data))
      .catch(err => setError(err?.response?.data?.message || err.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, [periodStart, periodEnd, showMasked]);

  async function toggleMask(u) {
    // CDD : le DÉMASQUAGE exige une nouvelle période de contrat (garde 17-08-2026)
    let body = {};
    if (u.masked && (u.contractType === 'CDD' || u.contractType === 'INTERIM')) {
      const cs = window.prompt(`${u.name} est en ${u.contractType === 'INTERIM' ? 'intérim' : 'CDD'}.\nNouvelle période de contrat — date DÉBUT (AAAA-MM-JJ) :`, todayStr);
      if (cs === null) return;
      const ce = window.prompt('Date FIN du contrat (AAAA-MM-JJ) :', cs);
      if (ce === null) return;
      body = { contractStart: cs.trim(), contractEnd: ce.trim() };
      // Férié dans la période ? → proposer la découpe en 2 contrats (18-08-2026)
      try {
        const chk = await usersApi.contractFerieCheck(body.contractStart, body.contractEnd);
        if (chk.feries?.length) {
          const fList = chk.feries.map(f => `${f.name} (${f.date})`).join(', ');
          const segTxt = (chk.split || []).map(s => `${s.start} → ${s.end}`).join('  +  ');
          body.splitAroundFerie = window.confirm(
            `⚠️ La période contient un jour férié : ${fList}.\n\nScinder en ${chk.split?.length || 2} contrats pour ne pas payer le férié (~200 €) ?\n${segTxt}\n\nOK = scinder · Annuler = garder 1 seul contrat (férié payé)`
          );
        }
      } catch (_) { /* check best-effort */ }
      const motif = window.prompt('Motif de la demande (optionnel — apparaîtra dans le mail contrats) :', '');
      if (motif === null) return;
      if (motif.trim()) body.motif = motif.trim();
    }
    try {
      await usersApi.toggleMask(u.userId, body);
      const d = await timesheetsGlobal.getGrid(ymd(periodStart), ymd(periodEnd), showMasked);
      setData(d);
    } catch (e) { alert('Erreur: ' + (e?.response?.data?.message || e.message)); }
  }

  function navMonth(delta) {
    const newStart = new Date(Date.UTC(periodStart.getUTCFullYear(), periodStart.getUTCMonth() + delta, 1));
    setPeriodStart(newStart);
    setPeriodEnd(endOfMonth(newStart));
  }
  function navWeek(delta) {
    setPeriodStart(addDays(periodStart, delta * 7));
    setPeriodEnd(addDays(periodEnd, delta * 7));
  }
  function goToday() {
    setPeriodStart(startOfMonth(today));
    setPeriodEnd(endOfMonth(today));
  }

  const todayStr = ymd(today);
  const filtered = useMemo(() => {
    if (!data?.users) return [];
    const q = filterText.trim().toLowerCase();
    // La réponse arrive déjà triée et `filter` garde l'ordre : re-trier à
    // chaque frappe coûterait une centaine de comparaisons par touche.
    if (!q) return data.users;
    return data.users.filter(u => u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q));
  }, [data, filterText]);

  if (loading) return <div style={{ padding: 40 }}>Chargement...</div>;
  if (error) return <div style={{ padding: 40, color: 'red' }}>Erreur: {error}</div>;
  if (!data) return null;

  return (
    <div style={{ padding: embedded ? 0 : 16, fontSize: 13 }}>
      {!embedded && <h1 style={{ marginTop: 0 }}>Toutes les feuilles de temps</h1>}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={goToday} style={btnStyle}>Aujourd'hui</button>
        <button onClick={() => navMonth(-1)} style={btnStyle}>◀</button>
        <span style={{ fontWeight: 600 }}>
          {MONTHS_FR_SHORT[periodStart.getUTCMonth()]} {periodStart.getUTCFullYear()}
        </span>
        <button onClick={() => navMonth(1)} style={btnStyle}>▶</button>
        <button onClick={() => navWeek(-1)} style={{ ...btnStyle, fontSize: 11 }}>-7j</button>
        <button onClick={() => navWeek(1)} style={{ ...btnStyle, fontSize: 11 }}>+7j</button>
        {canExport && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderLeft: '1px solid #e5e7eb', paddingLeft: 12 }}>
            <label style={{ fontSize: 12, color: '#475569' }}>Export du</label>
            <input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4 }} />
            <label style={{ fontSize: 12, color: '#475569' }}>au</label>
            <input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)} style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4 }} />
            <button onClick={handleExportPeriod} disabled={exporting}
              title="Exporter toutes les feuilles de temps de la période — tous les utilisateurs (y compris masqués), Excel multi-onglets"
              style={{ ...btnStyle, background: '#15803d', color: '#fff', borderColor: '#15803d' }}>
              {exporting ? 'Export…' : '⭳ Exporter (XLSX)'}
            </button>
          </span>
        )}
        <input
          type="text"
          placeholder="Rechercher employé..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, marginLeft: 'auto', minWidth: 200 }}
        />
        {canMask && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#475569' }}>
            <input type="checkbox" checked={showMasked} onChange={e => setShowMasked(e.target.checked)} />
            Afficher masqués
          </label>
        )}
        {canPayrollCheck && (
          <span style={{ fontSize: 12, fontWeight: 600, color: '#15803d', background: '#dcfce7', padding: '3px 10px', borderRadius: 12 }}
            title="Lignes marquées « fait » pour la déclaration de salaire (ce mois)">
            ✓ {filtered.filter(u => doneSet.has(u.userId)).length} / {filtered.length} faits
          </span>
        )}
      </div>

      {/* Grid */}
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, whiteSpace: 'nowrap', minWidth: '100%' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={{ ...thStyle, position: 'sticky', left: 0, backgroundColor: '#f9fafb', zIndex: 2, textAlign: 'left', minWidth: 300, borderRight: '2px solid #374151' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14 }}>
                  <span>Employé</span>
                  <span style={{ textAlign: 'right', lineHeight: 1.1 }}>
                    <div style={{ fontWeight: 700 }}>H supp.</div>
                    <div style={{ fontWeight: 400, fontSize: 9, color: '#9ca3af' }}><span style={{ color: '#15803d' }}>+ à payer</span> / <span style={{ color: '#b91c1c' }}>− à retirer</span></div>
                  </span>
                </div>
              </th>
              {data.days.map(d => {
                const dd = parseYMD(d);
                const isToday = d === todayStr;
                const we = isWeekend(d);
                return (
                  <th key={d} style={{
                    ...thStyle,
                    backgroundColor: isToday ? '#dbeafe' : (we ? '#f3f4f6' : '#f9fafb'),
                    textAlign: 'center',
                    minWidth: 50,
                  }}>
                    <div style={{ fontSize: 10, color: '#6b7280' }}>{DAYS_FR[dd.getUTCDay()]}</div>
                    <div style={{ fontWeight: 600 }}>{dd.getUTCDate()}</div>
                  </th>
                );
              })}
              <th style={{ ...thStyle, backgroundColor: '#dcfce7', textAlign: 'center', minWidth: 70 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => {
              const isDone = doneSet.has(u.userId);
              return (
              <tr key={u.userId} style={{ borderTop: '1px solid #e5e7eb', ...(u.masked ? { opacity: 0.55 } : {}) }}>
                <td style={{ ...tdStyle, position: 'sticky', left: 0, backgroundColor: isDone ? '#d1d5db' : (u.masked ? '#f1f5f9' : '#fff'), zIndex: 1, fontWeight: 500, borderRight: '2px solid #374151' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      {canPayrollCheck && (
                        <input type="checkbox" checked={isDone} onChange={() => toggleDone(u.userId)}
                          title="Marquer « fait » (pointage déclaration salaire)"
                          style={{ marginRight: 8, transform: 'scale(1.15)', verticalAlign: 'middle', cursor: 'pointer' }} />
                      )}
                      {u.name}{marqueurInactif(u)}
                      {isDone && <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '1px 7px', borderRadius: 10, marginLeft: 8 }}>✓ fait</span>}
                      {u.masked && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4, fontStyle: 'italic' }}>(masqué)</span>}
                      {canMask && (
                        <button onClick={() => toggleMask(u)} title={u.masked ? 'Réafficher partout' : 'Masquer (données conservées)'}
                          style={{ marginLeft: 6, padding: '1px 6px', fontSize: 10, border: '1px solid #d1d5db', borderRadius: 4, background: u.masked ? '#dcfce7' : '#fff', cursor: 'pointer' }}>
                          {u.masked ? '👁 Afficher' : '🚫 Masquer'}
                        </button>
                      )}
                    </span>
                    {/* Décision HR overtime : + à payer (vert) / − à retirer du salaire (rouge) */}
                    {(() => {
                      const od = u.otDecision;
                      const pending = od && od.status === 'PENDING';
                      if (!od || (od.payHours <= 0 && od.deductHours <= 0)) {
                        return <span style={{ fontSize: 11, color: '#9ca3af' }}>—</span>;
                      }
                      return (
                        <span style={{ textAlign: 'right', lineHeight: 1.25, whiteSpace: 'nowrap', fontStyle: pending ? 'italic' : 'normal', opacity: pending ? 0.7 : 1 }}>
                          {od.payHours > 0 && <div style={{ color: '#15803d', fontWeight: 700 }} title="Heures sup. à payer">+{fmtHours(od.payHours)}</div>}
                          {od.deductHours > 0 && <div style={{ color: '#b91c1c', fontWeight: 700 }} title="Heures à retirer du salaire">−{fmtHours(od.deductHours)}</div>}
                          {pending && <div style={{ color: '#d97706', fontSize: 9 }}>⏳ en attente</div>}
                        </span>
                      );
                    })()}
                  </div>
                </td>
                {data.days.map(d => {
                  const cell = u.days[d];
                  const min = cell?.min || 0;
                  const leaveAutoMin = cell?.leaveAutoMin || 0;
                  const workMin = cell?.workMin || 0;
                  const hasDraft = cell?.hasDraft;
                  const we = isWeekend(d);
                  const isToday = d === todayStr;
                  const absCode = cell?.absenceCode;
                  const absLabel = cell?.absenceLabel;
                  const absStatus = cell?.absenceStatus;
                  const absHalfDay = cell?.absenceHalfDay;
                  const absStyle = absCode ? absenceStyle(absCode) : null;
                  const showAbsence = !!absStyle;
                  const hasWorkOverlap = showAbsence && workMin > 0;
                  const absMin = leaveAutoMin;

                  // Day 11 (28-05-2026) — tooltip intelligent
                  let tooltip = '';
                  if (showAbsence) {
                    tooltip = `${absStyle.label}${absHalfDay ? ' ½' : ''}`
                      + (absMin > 0 ? ` ${formatMin(absMin)}` : '')
                      + (hasWorkOverlap ? ` + TRAV ${formatMin(workMin)}` : '')
                      + (absLabel ? ` — ${absLabel}` : '')
                      + (absStatus && absStatus !== 'APPROVED' ? ` (${absStatus})` : '');
                  } else if (min === 0 && !we) {
                    tooltip = `Aucune heure encodée — il manque ${formatMin(STANDARD_DAY_MIN)} pour atteindre 7:36`;
                  } else if (min > 0 && min < STANDARD_DAY_MIN) {
                    tooltip = `${formatMin(min)} encodé — il manque ${formatMin(STANDARD_DAY_MIN - min)} pour atteindre 7:36`;
                  } else if (min === STANDARD_DAY_MIN) {
                    tooltip = `Journée complète (${formatMin(min)})`;
                  } else if (min > STANDARD_DAY_MIN) {
                    tooltip = `Heures supplémentaires : +${formatMin(min - STANDARD_DAY_MIN)} (total ${formatMin(min)})`;
                  }

                  // Marquage CDD (17-08-2026, variante B+C) : hors contrat hachuré + « HC » ; contrat souligné orange
                  // CDD successifs: TOUTES les périodes (historique)
                  const cddPeriods = (u.contracts && u.contracts.length)
                    ? u.contracts
                    : (u.contractStart && u.contractEnd ? [{ startDate: u.contractStart, endDate: u.contractEnd }] : []);
                  const isCdd = (u.contractType === 'CDD' || u.contractType === 'INTERIM') && cddPeriods.length > 0; // M-011: intérim marqué comme CDD
                  const inContract = isCdd && cddPeriods.some(c => d >= c.startDate && d <= c.endDate);
                  const outContract = isCdd && !inContract;

                  return (
                    <td
                      key={d}
                      onClick={() => (min > 0 || showAbsence) && setDayModal({ userId: u.userId, userName: u.name, date: d })}
                      style={{
                        ...tdStyle,
                        textAlign: 'center',
                        // Fond neutre TOUJOURS — sauf ligne « fait » (grisée)
                        backgroundColor: isDone ? '#e5e7eb' : (isToday ? '#eff6ff' : (we ? '#fafafa' : '#fff')),
                        ...(outContract ? { backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 4px, rgba(100,116,139,.20) 4px 7px)' } : {}),
                        ...(inContract ? { boxShadow: 'inset 0 -3px 0 0 #d97706' } : {}),
                        // Texte coloré selon absence OU selon nb d'heures
                        color: showAbsence ? absStyle.color : colorForMin(min),
                        cursor: (min > 0 || showAbsence) ? 'pointer' : 'default',
                        position: 'relative',
                        fontWeight: (min > 0 || showAbsence) ? 700 : 400,
                        padding: showAbsence ? '2px 4px' : undefined,
                        lineHeight: showAbsence ? '1.15' : undefined,
                      }}
                      title={outContract && !tooltip ? 'Hors contrat (CDD)' : tooltip}
                    >
                      {(outContract && min === 0 && !showAbsence) ? (
                        <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 10 }}>HC</span>
                      ) : showAbsence ? (
                        <>
                          <div style={{ fontSize: 11, lineHeight: 1.1 }}>
                            {absStyle.label}{absHalfDay && <span style={{ fontSize: 9 }}> ½</span>}
                          </div>
                          {absMin > 0 && (
                            <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.85, marginTop: 1 }}>
                              {formatMin(absMin)}
                            </div>
                          )}
                          {hasWorkOverlap && (
                            <div style={{ fontSize: 9, marginTop: 1, color: colorForMin(workMin), opacity: 0.9 }}>
                              +{formatMin(workMin)}
                            </div>
                          )}
                        </>
                      ) : formatMin(min)}
                      {hasDraft && !showAbsence && <span style={{
                        position: 'absolute', top: 2, right: 4, width: 6, height: 6,
                        borderRadius: '50%', backgroundColor: '#fbbf24',
                      }} />}
                    </td>
                  );
                })}
                <td style={{ ...tdStyle, backgroundColor: isDone ? '#e5e7eb' : '#f0fdf4', textAlign: 'center', fontWeight: 600 }}>
                  {formatMin(u.totalMin)}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280' }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, backgroundColor: '#fbbf24', borderRadius: '50%', marginRight: 4 }} />
        Cellule contient des entries non-approuvées (DRAFT/SUBMITTED)
      </div>

      {/* Day 11 — Légende absences */}
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 11 }}>
        <span style={{ fontWeight: 600, color: '#374151', marginRight: 4 }}>Légende :</span>
        {Object.entries(ABSENCE_STYLE).filter(([k]) => !['VAEN1','VA-0.5','AA-0.5','CSSO','VAN','MAT','PAT','CSS'].includes(k) && (ABSENCE_LEGEND[k] || k === 'HC')).map(([code, st]) => (
          <span key={code} style={{
            color: st.color, padding: '2px 8px',
            border: `1px solid ${st.color}`, borderRadius: 4,
            fontWeight: 700, background: '#fff',
          }}>
            {st.label} {ABSENCE_LEGEND[code] && <span style={{ fontWeight: 400, color: '#6b7280' }}>— {ABSENCE_LEGEND[code]}</span>}
          </span>
        ))}
        <span style={{
          padding: '2px 8px', borderRadius: 4, fontWeight: 700,
          background: '#fff', color: '#7c3aed',
          border: '1px solid #7c3aed',
        }}>½ — demi-journée</span>
      </div>

      {dayModal && <DayModal {...dayModal} onClose={() => setDayModal(null)} onRefetch={() => {
        // refresh grid after edit/delete
        setLoading(true);
        timesheetsGlobal.getGrid(ymd(periodStart), ymd(periodEnd)).then(d => setData(d)).finally(() => setLoading(false));
      }} />}
    </div>
  );
}

// ---------------------------------------------------------------------
// MODAL: Detalii zi pentru un user
// ---------------------------------------------------------------------
function DayModal({ userId, userName, date, onClose, onRefetch }) {
  const [entries, setEntries] = useState(null);
  const [err, setErr] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ durationMin: 0, description: '' });
  const [busy, setBusy] = useState(false);

  const loadEntries = () => {
    setErr(null);
    timesheetsGlobal.getDay(userId, date).then(data => setEntries(data))
      .catch(e => setErr(e?.response?.data?.message || e.message));
  };
  useEffect(() => { loadEntries(); }, [userId, date]);

  const totalMin = entries ? entries.reduce((s, e) => s + e.durationMin, 0) : 0;

  function startEdit(entry) {
    setEditingId(entry.id);
    setEditForm({ durationMin: entry.durationMin, description: entry.description || '' });
  }
  function cancelEdit() {
    setEditingId(null);
    setEditForm({ durationMin: 0, description: '' });
  }
  async function saveEdit() {
    setBusy(true);
    try {
      await timesheetsGlobal.updateEntry(editingId, {
        durationMin: parseInt(editForm.durationMin, 10),
        description: editForm.description,
      });
      cancelEdit();
      loadEntries();
      onRefetch?.();
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
    } finally { setBusy(false); }
  }
  async function deleteEntry(entry) {
    const isLeaveAuto = entry.description?.includes('Auto-cree depuis Leave') || entry.description?.includes('Auto-créé depuis Leave');
    const isAttSplit = entry.description?.includes('ATTENDANCE_SPLIT');
    const msg = isLeaveAuto
      ? `⚠️ Cette entrée est AUTO-générée depuis un Leave APPROUVÉ.\nLa suppression va aussi:\n  • Annuler le Leave (single-day)\n  • Restaurer le solde de congé\n  • Supprimer l'Attendance liée\n\nContinuer ?`
      : `Supprimer cette entrée (${entry.projectName} / ${entry.taskName}, ${entry.durationLabel}) ?`;
    if (!confirm(msg)) return;
    setBusy(true);
    try {
      const result = await timesheetsGlobal.deleteEntry(entry.id);
      if (result.leaveCancelled) {
        alert(`✓ Entrée supprimée. Leave #${result.leaveId} aussi annulé (cascade).`);
      }
      loadEntries();
      onRefetch?.();
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: '#fff', borderRadius: 8, padding: 20, maxWidth: '95vw',
        maxHeight: '90vh', overflow: 'auto', minWidth: 900,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>
            {userName} — {date}
            <span style={{ marginLeft: 12, fontSize: 14, color: '#6b7280', fontWeight: 400 }}>
              Total: {formatMin(totalMin)} ({entries?.length || 0} entries)
            </span>
          </h2>
          <button onClick={onClose} style={{ ...btnStyle, fontSize: 16 }}>✕</button>
        </div>

        {err && <div style={{ color: 'red', marginBottom: 8 }}>Erreur: {err}</div>}
        {!entries && !err && <div>Chargement...</div>}
        {entries && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Employé</th>
                <th style={thStyle}>Chef projet</th>
                <th style={thStyle}>Statut</th>
                <th style={thStyle}>Projet</th>
                <th style={thStyle}>Tâche</th>
                <th style={thStyle}>Description</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Temps</th>
                <th style={{ ...thStyle, textAlign: 'center', minWidth: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
                const isEditing = editingId === e.id;
                return (
                  <tr key={e.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={tdStyle}>{e.date}</td>
                    <td style={tdStyle}>{e.employeeName}</td>
                    <td style={tdStyle}>{e.chefProjet}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 6px', borderRadius: 4, fontSize: 11,
                        backgroundColor: STATUS_COLORS[e.status] + '33',
                        color: STATUS_COLORS[e.status],
                        fontWeight: 600,
                      }}>{e.statusLabel}</span>
                    </td>
                    <td style={tdStyle}>{e.projectName}</td>
                    <td style={tdStyle}>{e.taskName}</td>
                    <td style={{ ...tdStyle, maxWidth: 250 }}>
                      {isEditing ? (
                        <input type="text" value={editForm.description}
                          onChange={ev => setEditForm({ ...editForm, description: ev.target.value })}
                          style={{ width: '100%', padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4 }} />
                      ) : (
                        <span title={e.description} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {e.description}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <input type="number" min="0" max="1440" value={editForm.durationMin}
                          onChange={ev => setEditForm({ ...editForm, durationMin: ev.target.value })}
                          style={{ width: 70, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, textAlign: 'right' }} />
                      ) : e.durationLabel}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} disabled={busy} style={{ ...btnStyle, background: '#10b981', color: '#fff', borderColor: '#10b981', marginRight: 4 }}>
                            ✓ Save
                          </button>
                          <button onClick={cancelEdit} disabled={busy} style={btnStyle}>
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(e)} disabled={busy} style={{ ...btnStyle, marginRight: 4 }} title="Modifier durée + description">
                            ✏️ Modifier
                          </button>
                          <button onClick={() => deleteEntry(e)} disabled={busy} style={{ ...btnStyle, background: '#fee2e2', color: '#b91c1c', borderColor: '#fecaca' }} title="Supprimer (cascade si LEAVE_AUTO)">
                            🗑️ Suppr.
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ backgroundColor: '#f9fafb', fontWeight: 600 }}>
                <td colSpan={7} style={{ ...tdStyle, textAlign: 'right' }}>TOTAL:</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMin(totalMin)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 12, fontSize: 11, color: '#6b7280' }}>
          💡 Modifier permet d'ajuster durée + description. Suppression d'une entrée LEAVE_AUTO annule aussi le Leave parent (cascade).
        </div>
      </div>
    </div>
  );
}

const thStyle = { padding: '6px 10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, fontSize: 11, textAlign: 'left' };
const tdStyle = { padding: '6px 10px' };
const btnStyle = { padding: '4px 10px', border: '1px solid #d1d5db', backgroundColor: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 12 };
